import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Package } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { EASE } from "@/components/Reveal";

const QUANTITY_OPTIONS = ["25–50", "50–100", "100–250", "250–500", "500–1,000", "1,000+"];
const BUDGET_OPTIONS = ["Under ₹500", "₹500 – ₹1,000", "₹1,000 – ₹2,500", "₹2,500 – ₹5,000", "₹5,000+"];
const OCCASION_OPTIONS = ["Employee Appreciation", "Festive — Diwali", "Festive — New Year", "Client Gifting", "Executive Gifting", "Event / Conference", "Other"];
const CATEGORY_OPTIONS = ["Luxury Glassware", "Premium Cutlery", "Kitchen Appliances", "Cookware", "Corporate Gift Sets", "Executive Gifts", "Festive Gifting", "Employee Gifting", "Not sure yet"];
const BRAND_OPTIONS = ["No preference", "CELLO", "VENUS", "AGARO"];

const inputCls =
  "w-full bg-transparent border border-white/15 focus:border-champagne text-ivory placeholder:text-ivory/30 text-sm px-4 py-3.5 outline-none transition-colors duration-300";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.25em] text-ivory/50 mb-2";

function Field({ label, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function EnquiryForm() {
  const [searchParams] = useSearchParams();
  const { list, clearList } = useStore();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "", designation: "",
    quantity: "", budget_per_gift: "", occasion: "", cities: "", delivery_date: "",
    category: "", brand: "", custom_branding: false, packaging: "", notes: "",
  });
  const [contextProduct, setContextProduct] = useState(null);

  useEffect(() => {
    const pid = searchParams.get("product");
    if (pid) api.get(`/products/${pid}`).then((r) => setContextProduct(r.data)).catch(() => {});
  }, [searchParams]);

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const products = [
    ...(contextProduct && !list.some((p) => p.id === contextProduct.id)
      ? [{ id: contextProduct.id, name: contextProduct.name, qty: 0 }]
      : []),
    ...list.map((p) => ({ id: p.id, name: p.name, qty: p.qty })),
  ];

  const canNext = step === 0 ? form.full_name.trim() && form.email.trim() : true;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post("/enquiries", {
        ...form,
        products: products.map((p) => ({ product_id: p.id, name: p.name, quantity: p.qty })),
      });
      setDone(true);
      clearList();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="enquiry-success"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="text-center py-16"
      >
        <div className="w-14 h-14 mx-auto border border-champagne flex items-center justify-center mb-8">
          <Check size={22} className="text-champagne" />
        </div>
        <h3 className="font-serif text-3xl text-ivory mb-4">Thank you.</h3>
        <p className="text-ivory/60 max-w-md mx-auto text-sm leading-relaxed">
          Our gifting team will review your requirements and get back to you shortly.
        </p>
      </motion.div>
    );
  }

  const steps = ["About You", "The Brief", "Review & Send"];

  return (
    <div data-testid="enquiry-form">
      <div className="flex items-center gap-4 mb-10">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <span className={`text-[10px] font-mono tracking-[0.2em] ${i === step ? "text-champagne" : "text-ivory/30"}`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className={`hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] ${i === step ? "text-ivory" : "text-ivory/30"}`}>{s}</span>
            {i < steps.length - 1 && <span className="w-8 sm:w-14 h-px bg-white/15" />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name *" htmlFor="eq-name">
                <input id="eq-name" data-testid="enquiry-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
              </Field>
              <Field label="Company Name" htmlFor="eq-company">
                <input id="eq-company" data-testid="enquiry-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company" />
              </Field>
              <Field label="Work Email *" htmlFor="eq-email">
                <input id="eq-email" data-testid="enquiry-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@company.com" />
              </Field>
              <Field label="Phone Number" htmlFor="eq-phone">
                <input id="eq-phone" data-testid="enquiry-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
              </Field>
              <Field label="Designation" htmlFor="eq-designation">
                <input id="eq-designation" data-testid="enquiry-designation" className={inputCls} value={form.designation} onChange={set("designation")} placeholder="e.g. HR Manager" />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Number of Gifts Required" htmlFor="eq-qty">
                <select id="eq-qty" data-testid="enquiry-quantity" className={inputCls} value={form.quantity} onChange={set("quantity")}>
                  <option value="" className="bg-charcoal">Select range</option>
                  {QUANTITY_OPTIONS.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                </select>
              </Field>
              <Field label="Approximate Budget Per Gift" htmlFor="eq-budget">
                <select id="eq-budget" data-testid="enquiry-budget" className={inputCls} value={form.budget_per_gift} onChange={set("budget_per_gift")}>
                  <option value="" className="bg-charcoal">Select range</option>
                  {BUDGET_OPTIONS.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                </select>
              </Field>
              <Field label="Occasion" htmlFor="eq-occasion">
                <select id="eq-occasion" data-testid="enquiry-occasion" className={inputCls} value={form.occasion} onChange={set("occasion")}>
                  <option value="" className="bg-charcoal">Select occasion</option>
                  {OCCASION_OPTIONS.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                </select>
              </Field>
              <Field label="Delivery City / Cities" htmlFor="eq-cities">
                <input id="eq-cities" data-testid="enquiry-cities" className={inputCls} value={form.cities} onChange={set("cities")} placeholder="e.g. Mumbai, Bengaluru" />
              </Field>
              <Field label="Required Delivery Date" htmlFor="eq-date">
                <input id="eq-date" data-testid="enquiry-delivery-date" type="date" className={inputCls} value={form.delivery_date} onChange={set("delivery_date")} />
              </Field>
              <Field label="Preferred Product Category" htmlFor="eq-category">
                <select id="eq-category" data-testid="enquiry-category" className={inputCls} value={form.category} onChange={set("category")}>
                  <option value="" className="bg-charcoal">Select category</option>
                  {CATEGORY_OPTIONS.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                </select>
              </Field>
              <Field label="Preferred Brand" htmlFor="eq-brand">
                <select id="eq-brand" data-testid="enquiry-brand" className={inputCls} value={form.brand} onChange={set("brand")}>
                  {BRAND_OPTIONS.map((o) => <option key={o} value={o === "No preference" ? "" : o} className="bg-charcoal">{o}</option>)}
                </select>
              </Field>
              <Field label="Packaging Requirements" htmlFor="eq-packaging">
                <input id="eq-packaging" data-testid="enquiry-packaging" className={inputCls} value={form.packaging} onChange={set("packaging")} placeholder="e.g. gift boxes, sleeves, message cards" />
              </Field>
              <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer select-none">
                <input data-testid="enquiry-custom-branding" type="checkbox" checked={form.custom_branding} onChange={set("custom_branding")} className="w-4 h-4 accent-[#C5A059]" />
                <span className="text-sm text-ivory/70">Custom branding required (logo, sleeves, inserts)</span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {products.length > 0 && (
                <div data-testid="enquiry-products-summary" className="border border-white/10 p-5">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne mb-4 flex items-center gap-2">
                    <Package size={13} /> Products in this enquiry
                  </p>
                  <ul className="space-y-2 text-sm text-ivory/70">
                    {products.map((p) => (
                      <li key={p.id} className="flex justify-between gap-4">
                        <span>{p.name}</span>
                        {p.qty > 0 && <span className="text-ivory/40 font-mono text-xs">× {p.qty}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Field label="Additional Requirements" htmlFor="eq-notes">
                <textarea id="eq-notes" data-testid="enquiry-notes" rows={4} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Anything else we should know — recipients, themes, timelines…" />
              </Field>
              {error && <p data-testid="enquiry-error" className="text-sm text-red-400">{error}</p>}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between items-center mt-10 pt-8 border-t border-white/10">
        <button
          data-testid="enquiry-back"
          onClick={() => setStep((s) => s - 1)}
          disabled={step === 0}
          className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-ivory/50 hover:text-ivory disabled:opacity-0 transition-all"
        >
          <ArrowLeft size={14} /> Back
        </button>
        {step < 2 ? (
          <button
            data-testid="enquiry-next"
            onClick={() => canNext && setStep((s) => s + 1)}
            className={`flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-3.5 transition-colors duration-300 ${
              canNext ? "bg-ivory text-charcoal hover:bg-champagne" : "bg-ivory/20 text-ivory/40 cursor-not-allowed"
            }`}
          >
            Continue <ArrowRight size={14} />
          </button>
        ) : (
          <button
            data-testid="enquiry-submit"
            onClick={submit}
            disabled={submitting}
            className="flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.2em] px-6 py-3.5 hover:bg-champagne-light transition-colors duration-300 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Request Corporate Quote"} <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
